import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getCentralToday, getCentralMonth } from "@/utils/dateHelpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TrendingUp, Calendar, DollarSign, Loader2, Save, Copy, Building2, Zap, FileDown, Plus, Trash2, Wrench, CheckCircle, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toField = (v, fallback = '') => (v !== null && v !== undefined ? String(v) : fallback);

const EXPENSE_GROUPS = [
  {
    label: "Utility Bills",
    icon: "zap",
    fields: [
      { key: 'water', label: 'Water ($)', placeholder: '100' },
      { key: 'gas', label: 'Gas ($)', placeholder: '150' },
      { key: 'electric', label: 'Electric ($)', placeholder: '400' },
      { key: 'telephone', label: 'Telephone ($)', placeholder: '80' },
      { key: 'internet', label: 'Internet ($)', placeholder: '100' },
      { key: 'cable_tv', label: 'Cable TV ($)', placeholder: '120' },
    ]
  },
  {
    label: "Fixed Costs",
    icon: "building",
    fields: [
      { key: 'mortgage', label: 'Mortgage/Loan ($)', placeholder: '2000' },
      { key: 'insurance', label: 'Insurance ($)', placeholder: '300' },
      { key: 'property_tax', label: 'Property Tax ($)', placeholder: '500' },
    ]
  },
  {
    label: "Operations",
    icon: "dollar",
    fields: [
      { key: 'api_travel_commission', label: 'API Travel Comm. ($)', placeholder: 'Auto-calculated', readOnly: true },
      { key: 'labor', label: 'Labor Cost ($)', placeholder: '1500' },
      { key: 'pest_control', label: 'Pest Control ($)', placeholder: '150' },
      { key: 'supplies_per_room', label: 'Supplies/Room/Night ($)', placeholder: '2.50' },
      { key: 'other', label: 'Others ($)', placeholder: '0' },
    ]
  }
];

const ALL_KEYS = EXPENSE_GROUPS.flatMap(g => g.fields.map(f => f.key));
const DEFAULTS = ALL_KEYS.reduce((acc, k) => { acc[k] = ''; return acc; }, {});
DEFAULTS.supplies_per_room = '2.50';

const DAILY_CATEGORIES = [
  "Repair/Maintenance", "Plumbing", "Electrical", "HVAC",
  "Cleaning Supplies", "Guest Amenities", "Laundry",
  "Office Supplies", "Equipment", "Transportation", "Miscellaneous"
];

export function ProfitabilityTab({ portalSettings }) {
  const [profitabilityData, setProfitabilityData] = useState(null);
  const [profitabilityLoading, setProfitabilityLoading] = useState(false);
  const [profitabilityMonth, setProfitabilityMonth] = useState(getCentralMonth());
  const [monthlyExpenses, setMonthlyExpenses] = useState({...DEFAULTS});
  const [savingExpenses, setSavingExpenses] = useState(false);
  const [copyingExpenses, setCopyingExpenses] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Daily expenses state
  const [dailyExpenses, setDailyExpenses] = useState([]);
  const [dailyTotal, setDailyTotal] = useState(0);
  const [newDaily, setNewDaily] = useState({ date: getCentralToday(), category: DAILY_CATEGORIES[0], amount: '', description: '' });
  const [addingDaily, setAddingDaily] = useState(false);

  // Billing reconciliation state
  const [billingRecon, setBillingRecon] = useState(null);

  // Date-field toggle: 'check_out_date' (default; matches railroad billing report) or 'check_in_date'
  const [dateField, setDateField] = useState('check_out_date');

  const loadExpenses = useCallback(async (month) => {
    try {
      const res = await axios.get(`${API}/admin/expenses/${month}`);
      const d = res.data;
      const updated = {};
      ALL_KEYS.forEach(k => { updated[k] = toField(d[k], DEFAULTS[k]); });
      setMonthlyExpenses(updated);
    } catch { /* form stays at defaults */ }
  }, []);

  const loadDailyExpenses = useCallback(async (month) => {
    try {
      const res = await axios.get(`${API}/admin/daily-expenses/${month}`);
      setDailyExpenses(res.data.expenses || []);
      setDailyTotal(res.data.total || 0);
    } catch { setDailyExpenses([]); setDailyTotal(0); }
  }, []);

  useEffect(() => {
    loadExpenses(profitabilityMonth);
    loadDailyExpenses(profitabilityMonth);
  }, [profitabilityMonth, loadExpenses, loadDailyExpenses]);

  const loadReport = async () => {
    setProfitabilityLoading(true);
    try {
      const profitRes = await axios.get(`${API}/admin/profitability/${profitabilityMonth}?date_field=${dateField}`);
      setProfitabilityData(profitRes.data);
      const autoComm = profitRes.data?.summary?.auto_api_commission;
      if (autoComm !== undefined) {
        setMonthlyExpenses(prev => ({...prev, api_travel_commission: String(autoComm)}));
      }
      // Fetch billing records for reconciliation — use the same date_field so totals line up.
      const [year, mon] = profitabilityMonth.split('-').map(Number);
      const days = new Date(year, mon, 0).getDate();
      const start = `${profitabilityMonth}-01`;
      const end = `${profitabilityMonth}-${String(days).padStart(2, '0')}`;
      try {
        const billRes = await axios.get(`${API}/admin/records?start_date=${start}&end_date=${end}&date_field=${dateField}`);
        const records = billRes.data || [];
        const completed = records.filter(r => r.is_checked_out);
        const active = records.filter(r => !r.is_checked_out);
        const nightsBilled = completed.reduce((sum, r) => sum + (r.total_nights || 0), 0);
        setBillingRecon({ checkIns: records.length, completed: completed.length, active: active.length, nightsBilled });
      } catch { setBillingRecon(null); }
    } catch { toast.error('Failed to load profitability data'); }
    finally { setProfitabilityLoading(false); }
  };

  const saveExpenses = async () => {
    setSavingExpenses(true);
    try {
      const payload = { month: profitabilityMonth };
      ALL_KEYS.forEach(k => { payload[k] = parseFloat(monthlyExpenses[k]) || (k === 'supplies_per_room' ? 2.50 : 0); });
      await axios.post(`${API}/admin/expenses`, payload);
      toast.success('Expenses saved! Generating report...');
      try {
        const profitRes = await axios.get(`${API}/admin/profitability/${profitabilityMonth}?date_field=${dateField}`);
        setProfitabilityData(profitRes.data);
        const autoComm = profitRes.data?.summary?.auto_api_commission;
        if (autoComm !== undefined) {
          setMonthlyExpenses(prev => ({...prev, api_travel_commission: String(autoComm)}));
        }
      } catch { toast.error('Saved but failed to refresh report'); }
    } catch { toast.error('Failed to save expenses'); }
    finally { setSavingExpenses(false); }
  };

  const copyFromPreviousMonth = async () => {
    setCopyingExpenses(true);
    try {
      const [year, mon] = profitabilityMonth.split('-').map(Number);
      const prevMonth = mon === 1 ? `${year - 1}-12` : `${year}-${String(mon - 1).padStart(2, '0')}`;
      const res = await axios.get(`${API}/admin/expenses/${prevMonth}`);
      const d = res.data;
      const hasData = ALL_KEYS.some(k => k !== 'supplies_per_room' && (d[k] || 0) > 0);
      if (!hasData) {
        toast.error(`No expenses found for ${prevMonth}`);
      } else {
        const updated = {};
        ALL_KEYS.forEach(k => { updated[k] = toField(d[k], DEFAULTS[k]); });
        setMonthlyExpenses(updated);
        toast.success(`Copied expenses from ${prevMonth}. Click Save to confirm.`);
      }
    } catch { toast.error('Failed to load previous month expenses'); }
    finally { setCopyingExpenses(false); }
  };

  const addDailyExpense = async () => {
    if (!newDaily.amount || parseFloat(newDaily.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setAddingDaily(true);
    try {
      await axios.post(`${API}/admin/daily-expenses`, {
        date: newDaily.date,
        category: newDaily.category,
        amount: parseFloat(newDaily.amount),
        description: newDaily.description,
      });
      toast.success('Daily expense added');
      setNewDaily({ date: getCentralToday(), category: DAILY_CATEGORIES[0], amount: '', description: '' });
      await loadDailyExpenses(profitabilityMonth);
      // Refresh report if loaded
      if (profitabilityData) {
        const profitRes = await axios.get(`${API}/admin/profitability/${profitabilityMonth}?date_field=${dateField}`);
        setProfitabilityData(profitRes.data);
      }
    } catch { toast.error('Failed to add expense'); }
    finally { setAddingDaily(false); }
  };

  const deleteDailyExpense = async (id) => {
    try {
      await axios.delete(`${API}/admin/daily-expenses/${id}`);
      toast.success('Expense deleted');
      await loadDailyExpenses(profitabilityMonth);
      if (profitabilityData) {
        const profitRes = await axios.get(`${API}/admin/profitability/${profitabilityMonth}?date_field=${dateField}`);
        setProfitabilityData(profitRes.data);
      }
    } catch { toast.error('Failed to delete expense'); }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/admin/profitability/${profitabilityMonth}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hodler_inn_pnl_${profitabilityMonth}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to export PDF'); }
    finally { setExportingPdf(false); }
  };

  const s = profitabilityData?.summary;

  return (
    <>
      <div className="mb-6">
        <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight">Room Profitability</h1>
        <p className="text-vault-text-secondary font-manrope mt-1">Track expenses and profit per room</p>
      </div>

      {/* Month selector + actions */}
      <Card className="bg-vault-surface border-vault-border mb-6">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Month</label>
            <Input type="month" value={profitabilityMonth} onChange={(e) => setProfitabilityMonth(e.target.value)} className="bg-black/50 border-vault-border text-vault-text w-48" data-testid="profitability-month-input" />
          </div>
          <Button onClick={loadReport} className="bg-vault-gold text-black hover:bg-vault-gold/90" disabled={profitabilityLoading} data-testid="load-report-btn">
            {profitabilityLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}Load Report
          </Button>
          <div className="flex flex-col">
            <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Group By</label>
            <div className="flex items-center gap-1 bg-black/40 rounded-md p-0.5 border border-vault-border h-10" data-testid="profitability-date-field-toggle">
              <button
                onClick={() => { setDateField('check_out_date'); if (profitabilityData) setTimeout(loadReport, 0); }}
                className={`text-xs px-3 py-1.5 rounded transition-colors ${dateField === 'check_out_date' ? 'bg-vault-gold text-black font-medium' : 'text-vault-text-secondary hover:text-vault-text'}`}
                data-testid="profitability-checkout-btn"
                title="Bucket by checkout date — matches railroad billing report"
              >
                By Check-Out
              </button>
              <button
                onClick={() => { setDateField('check_in_date'); if (profitabilityData) setTimeout(loadReport, 0); }}
                className={`text-xs px-3 py-1.5 rounded transition-colors ${dateField === 'check_in_date' ? 'bg-vault-gold text-black font-medium' : 'text-vault-text-secondary hover:text-vault-text'}`}
                data-testid="profitability-checkin-btn"
                title="Bucket by check-in date — includes active stays"
              >
                By Check-In
              </button>
            </div>
          </div>
          <Button onClick={copyFromPreviousMonth} variant="outline" className="border-vault-border text-vault-text hover:bg-vault-surface-highlight" disabled={copyingExpenses} data-testid="copy-expenses-btn">
            {copyingExpenses ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Copy className="w-4 h-4 mr-2" />}Copy from Last Month
          </Button>
          {profitabilityData && (
            <Button onClick={exportPdf} variant="outline" className="border-vault-gold/50 text-vault-gold hover:bg-vault-gold/10" disabled={exportingPdf} data-testid="export-pdf-btn">
              {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}Download P&L PDF
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Monthly fixed expense entry - grouped */}
      {EXPENSE_GROUPS.map((group) => (
        <Card key={group.label} className="bg-vault-surface border-vault-border mb-4">
          <CardHeader className="border-b border-vault-border pb-3 py-3">
            <CardTitle className="font-outfit text-base text-vault-text flex items-center gap-2">
              {group.icon === 'zap' && <Zap className="w-4 h-4 text-vault-gold" />}
              {group.icon === 'building' && <Building2 className="w-4 h-4 text-vault-gold" />}
              {group.icon === 'dollar' && <DollarSign className="w-4 h-4 text-vault-gold" />}
              {group.label}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {group.fields.map(({ key, label, placeholder, readOnly }) => (
                <div key={key}>
                  <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">{label}</label>
                  <Input
                    type="number" step="0.01"
                    value={monthlyExpenses[key]}
                    onChange={(e) => !readOnly && setMonthlyExpenses({...monthlyExpenses, [key]: e.target.value})}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    className={`bg-black/50 border-vault-border text-vault-text ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                    data-testid={`expense-${key}`}
                  />
                  {readOnly && <p className="text-[10px] text-blue-400 mt-0.5">Auto: {portalSettings?.api_commission_rate || 10}% of railroad billing</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      <Button onClick={saveExpenses} className="mb-6 bg-emerald-600 hover:bg-emerald-500 text-white" disabled={savingExpenses} data-testid="save-expenses-btn">
        {savingExpenses ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save & Generate Report
      </Button>

      {/* Daily Variable Expenses */}
      <Card className="bg-vault-surface border-vault-border mb-6">
        <CardHeader className="border-b border-vault-border pb-3">
          <CardTitle className="font-outfit text-lg text-vault-text flex items-center gap-2">
            <Wrench className="w-5 h-5 text-vault-gold" />Daily Variable Expenses
            {dailyTotal > 0 && <span className="text-sm font-normal text-red-400 ml-auto">Total: ${dailyTotal.toLocaleString()}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {/* Add form */}
          <div className="flex flex-wrap gap-3 items-end mb-4 pb-4 border-b border-vault-border/50">
            <div>
              <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">Date</label>
              <Input type="date" value={newDaily.date} onChange={(e) => setNewDaily({...newDaily, date: e.target.value})} className="bg-black/50 border-vault-border text-vault-text w-40" data-testid="daily-date" />
            </div>
            <div>
              <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">Category</label>
              <select value={newDaily.category} onChange={(e) => setNewDaily({...newDaily, category: e.target.value})} className="bg-black/50 border border-vault-border text-vault-text rounded-md px-3 py-2 text-sm h-10" data-testid="daily-category">
                {DAILY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">Amount ($)</label>
              <Input type="number" step="0.01" value={newDaily.amount} onChange={(e) => setNewDaily({...newDaily, amount: e.target.value})} placeholder="150" className="bg-black/50 border-vault-border text-vault-text w-28" data-testid="daily-amount" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">Description</label>
              <Input value={newDaily.description} onChange={(e) => setNewDaily({...newDaily, description: e.target.value})} placeholder="Plumber repair, Room 105" className="bg-black/50 border-vault-border text-vault-text" data-testid="daily-description" />
            </div>
            <Button onClick={addDailyExpense} className="bg-vault-gold text-black hover:bg-vault-gold/90" disabled={addingDaily} data-testid="add-daily-btn">
              {addingDaily ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Add
            </Button>
          </div>

          {/* List */}
          {dailyExpenses.length === 0 ? (
            <p className="text-vault-text-secondary text-sm text-center py-3">No variable expenses recorded for this month</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="daily-expenses-table">
                <thead>
                  <tr className="border-b border-vault-border">
                    <th className="px-3 py-2 text-left text-xs text-vault-gold uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs text-vault-gold uppercase">Category</th>
                    <th className="px-3 py-2 text-left text-xs text-vault-gold uppercase">Description</th>
                    <th className="px-3 py-2 text-right text-xs text-vault-gold uppercase">Amount</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-vault-border/50">
                  {dailyExpenses.map((item) => (
                    <tr key={item.id} className="hover:bg-vault-surface-highlight/30">
                      <td className="px-3 py-2 text-sm text-vault-text">{item.date}</td>
                      <td className="px-3 py-2 text-sm text-vault-text-secondary">{item.category}</td>
                      <td className="px-3 py-2 text-sm text-vault-text-secondary">{item.description}</td>
                      <td className="px-3 py-2 text-sm text-red-400 text-right">${item.amount?.toLocaleString()}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => deleteDailyExpense(item.id)} className="text-red-400/60 hover:text-red-400" data-testid={`delete-daily-${item.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary cards */}
      {s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
            {[
              { label: 'Available Nights', value: s.total_available_room_nights, fmt: v => v },
              { label: 'Occupied Nights', value: s.total_occupied_nights, fmt: v => `${v} (${s.occupancy_percent}%)`, tooltip: `Railroad: ${s.total_railroad_nights || 0} | Non-Rail: ${s.total_non_railroad_nights || 0}` },
              { label: 'Revenue', value: s.total_revenue, fmt: v => `$${v.toLocaleString()}`, color: 'text-emerald-400' },
              { label: 'Fixed Expenses', value: s.total_fixed_expenses, fmt: v => `$${v.toLocaleString()}`, color: 'text-red-400' },
              { label: 'Variable Expenses', value: (s.total_supplies_cost || 0) + (s.total_daily_variable || 0), fmt: v => `$${v.toLocaleString()}`, color: 'text-orange-400' },
              { label: 'Daily Fixed Cost', value: s.daily_fixed_cost, fmt: v => `$${v.toLocaleString()}`, color: 'text-amber-400' },
              { label: 'Net Profit', value: s.total_profit, fmt: v => `$${v.toLocaleString()}`, color: (s.total_profit || 0) >= 0 ? 'text-vault-gold' : 'text-red-400' },
            ].map(({ label, value, fmt, color, tooltip }) => (
              <Card key={label} className="bg-vault-surface border-vault-border" title={tooltip || ''}>
                <CardContent className="p-4 text-center">
                  <p className="text-vault-text-secondary text-xs uppercase tracking-wider">{label}</p>
                  <p className={`text-xl font-bold mt-1 ${color || 'text-vault-text'}`}>{fmt(value || 0)}</p>
                  {tooltip && <p className="text-vault-text-secondary text-[10px] mt-1">{tooltip}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Billing vs Profitability Reconciliation */}
          {billingRecon && (
            <Card className="bg-vault-surface border-vault-border mb-6" data-testid="reconciliation-card">
              <CardHeader className="border-b border-vault-border pb-3">
                <CardTitle className="font-outfit text-sm text-vault-text flex items-center gap-2">
                  {Math.abs((s.total_railroad_nights || 0) - (billingRecon.nightsBilled + billingRecon.active)) <= 2
                    ? <CheckCircle className="w-4 h-4 text-emerald-400" />
                    : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                  Billing vs Profitability Reconciliation
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-6">
                  {/* Billing side */}
                  <div>
                    <p className="text-vault-text-secondary text-xs uppercase tracking-wider mb-3 font-medium">Billing Tab</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-vault-text-secondary">Check-Ins</span>
                        <span className="text-vault-text font-medium">{billingRecon.checkIns}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-vault-text-secondary">Completed Stays</span>
                        <span className="text-vault-text font-medium">{billingRecon.completed}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-vault-text-secondary">Active Stays</span>
                        <span className="text-cyan-400 font-medium">{billingRecon.active}</span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t border-vault-border">
                        <span className="text-vault-gold font-medium">Nights Billed</span>
                        <span className="text-vault-gold font-bold">{billingRecon.nightsBilled}</span>
                      </div>
                    </div>
                  </div>
                  {/* Profitability side */}
                  <div>
                    <p className="text-vault-text-secondary text-xs uppercase tracking-wider mb-3 font-medium">Profitability Tab</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-vault-text-secondary">Railroad Nights</span>
                        <span className="text-vault-text font-medium">{s.total_railroad_nights || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-vault-text-secondary">Non-Railroad Nights</span>
                        <span className="text-vault-text font-medium">{s.total_non_railroad_nights || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-vault-text-secondary">Includes Active</span>
                        <span className="text-cyan-400 font-medium">{billingRecon.active} stays</span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t border-vault-border">
                        <span className="text-vault-gold font-medium">Total Occupied</span>
                        <span className="text-vault-gold font-bold">{s.total_occupied_nights || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Status bar */}
                {(() => {
                  const rrNights = s.total_railroad_nights || 0;
                  const expected = billingRecon.nightsBilled + billingRecon.active;
                  const variance = rrNights - expected;
                  const isMatch = Math.abs(variance) <= 2;
                  return (
                    <div className={`mt-4 p-3 rounded-lg text-center text-xs font-medium ${isMatch ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-amber-500/10 border border-amber-500/30 text-amber-400'}`} data-testid="reconciliation-status">
                      {isMatch
                        ? `Railroad nights (${rrNights}) = Billed (${billingRecon.nightsBilled}) + Active (${billingRecon.active}) — Numbers match`
                        : `Variance: ${variance > 0 ? '+' : ''}${variance} nights — Railroad (${rrNights}) vs Billed+Active (${expected})`}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Cost breakdown per available room-night */}
          {profitabilityData.expenses_entered && profitabilityData.cost_breakdown && (
            <Card className="bg-vault-surface border-vault-border mb-6">
              <CardHeader className="border-b border-vault-border pb-3">
                <CardTitle className="font-outfit text-lg text-vault-text">Cost Per Available Room-Night ({s.total_rooms} rooms x {s.days_in_month} days = {s.total_available_room_nights})</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-3">
                  {[
                    { label: 'Water', key: 'water_per_night', color: 'text-blue-400' },
                    { label: 'Gas', key: 'gas_per_night', color: 'text-orange-400' },
                    { label: 'Electric', key: 'electric_per_night', color: 'text-yellow-400' },
                    { label: 'Telephone', key: 'telephone_per_night', color: 'text-teal-400' },
                    { label: 'Internet', key: 'internet_per_night', color: 'text-cyan-400' },
                    { label: 'Cable TV', key: 'cable_tv_per_night', color: 'text-indigo-400' },
                    { label: 'Mortgage', key: 'mortgage_per_night', color: 'text-red-400' },
                    { label: 'Insurance', key: 'insurance_per_night', color: 'text-pink-400' },
                    { label: 'Prop. Tax', key: 'property_tax_per_night', color: 'text-amber-400' },
                    { label: 'Travel Comm.', key: 'api_travel_commission_per_night', color: 'text-violet-400' },
                    { label: 'Labor', key: 'labor_per_night', color: 'text-purple-400' },
                    { label: 'Pest Control', key: 'pest_control_per_night', color: 'text-lime-400' },
                    { label: 'Other', key: 'other_per_night', color: 'text-gray-400' },
                    { label: 'Supplies', key: 'supplies_per_night', color: 'text-green-400' },
                  ].map(({ label, key, color }) => (
                    <div key={key} className="text-center p-3 bg-black/30 rounded-lg">
                      <p className="text-vault-text-secondary text-xs">{label}</p>
                      <p className={`text-lg font-bold ${color}`}>${profitabilityData.cost_breakdown[key] || 0}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-center p-3 bg-vault-gold/20 rounded-lg border border-vault-gold/30 max-w-xs mx-auto">
                  <p className="text-vault-gold text-xs font-medium">TOTAL COST / ROOM-NIGHT</p>
                  <p className="text-xl font-bold text-vault-gold">${s.cost_per_room_night || 0}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Profit by room table */}
          <Card className="bg-vault-surface border-vault-border">
            <CardHeader className="border-b border-vault-border pb-3">
              <CardTitle className="font-outfit text-lg text-vault-text">Profit by Room</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="profit-by-room-table">
                  <thead className="bg-vault-surface-highlight border-b border-vault-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Room</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-vault-gold uppercase tracking-wider">Nights</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-vault-gold uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-vault-gold uppercase tracking-wider">Cost</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-vault-gold uppercase tracking-wider">Profit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-vault-gold uppercase tracking-wider">Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-vault-border">
                    {(profitabilityData.rooms || []).map((room) => (
                      <tr key={room.room_number} className="hover:bg-vault-surface-highlight/50">
                        <td className="px-4 py-3 text-sm font-medium text-vault-text">{room.room_number}</td>
                        <td className="px-4 py-3 text-sm text-vault-text-secondary">{room.room_type}</td>
                        <td className="px-4 py-3 text-sm text-vault-text text-right">{room.nights}</td>
                        <td className="px-4 py-3 text-sm text-emerald-400 text-right">${(room.revenue || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-red-400 text-right">${(room.total_cost || 0).toLocaleString()}</td>
                        <td className={`px-4 py-3 text-sm font-medium text-right ${(room.profit || 0) >= 0 ? 'text-vault-gold' : 'text-red-400'}`}>${(room.profit || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          {room.nights > 0 ? (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${room.margin_percent >= 70 ? 'bg-emerald-500/20 text-emerald-400' : room.margin_percent >= 50 ? 'bg-vault-gold/20 text-vault-gold' : room.margin_percent >= 30 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>{room.margin_percent}%</span>
                          ) : <span className="text-vault-text-secondary text-xs">-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-vault-surface-highlight border-t border-vault-border">
                    <tr>
                      <td colSpan={2} className="px-4 py-3 text-sm font-bold text-vault-gold">TOTAL</td>
                      <td className="px-4 py-3 text-sm font-bold text-vault-text text-right">{s.total_occupied_nights || 0}</td>
                      <td className="px-4 py-3 text-sm font-bold text-emerald-400 text-right">${(s.total_revenue || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm font-bold text-red-400 text-right">${(s.total_cost || 0).toLocaleString()}</td>
                      <td className={`px-4 py-3 text-sm font-bold text-right ${(s.total_profit || 0) >= 0 ? 'text-vault-gold' : 'text-red-400'}`}>${(s.total_profit || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right"><span className="px-2 py-1 rounded text-xs font-bold bg-vault-gold/30 text-vault-gold">{s.overall_margin_percent || 0}%</span></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!profitabilityData && !profitabilityLoading && (
        <Card className="bg-vault-surface border-vault-border">
          <CardContent className="p-8 text-center">
            <TrendingUp className="w-12 h-12 text-vault-text-secondary mx-auto mb-4" />
            <p className="text-vault-text-secondary">Select a month and click "Load Report" to view profitability data</p>
          </CardContent>
        </Card>
      )}
    </>
  );
}
